import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '@/editor/math/Line2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { equalsNumber, normalizeAngle } from '@/editor/math/utils';
import { limitAngle } from './utils';
import { RADIAN_ANGLE_THRESHOLD } from '@/editor/constant/math';
import { EBoxSide, IRotatedBox } from '@/editor/types';
import { globalStores } from '@/editor/stores';
import {
  filterVisibleInfos,
  getAbsorbFilterBounds,
  getAbsorbViewportBounds,
  TAbsorbViewportBounds,
  TAbsorbViewportBoundsProvider
} from '../viewportFilter';
import type { BoundingBox } from '@/editor/utils/dxf/types';

export type { IRotatedBox } from '@/editor/types';

enum EPointOnLineSide {
  Left = 1,
  Right = -1,
  On = 0
}

export enum EAbsorbAxis {
  X = 'x',
  Y = 'y',
  Center = 'center'
}

interface IAbsorbSegInfo {
  seg: LineSeg2d;
  foot: IPoint2d;
  // centerDistance: number; // 垂点到盒子中心的距离
  diffDistance: number;
  targetToFootDistance: number; // 吸附打平时，用目标中点/有限线段到垂足距离排序
  absorbDistance: number; // 吸附距离，thiness/2 或 width/2
  side: EPointOnLineSide;
  axis?: EAbsorbAxis;
  rotationAngle: number;
  angle: number;
  weight: number;
  absorbOnPreferredSide?: boolean;
}

export interface ICenterLineInfo {
  lineCenter: IPoint2d;
  line: Line2d;
  angle: number;
  /** 所属box的包围盒，供视口过滤使用，见 IViewportFilterable.bounds */
  bounds?: BoundingBox;
}

export interface IBoxBoundingLineInfo {
  seg: LineSeg2d;
  line: Line2d;
  angle: number; // 线段角度 [0, Math.PI)
  rotatable: boolean; // 该线段是否可改变box旋转方向
  onlyAbsorbSeg: boolean; // 是否只能吸附到线段上
  onlyOnLeftSide?: boolean; // 是否只能吸附在右侧，onlyOnLeftSide和onlyOnCenter不能同时为true
  onlyOnCenter?: boolean; // 是否只能吸附在线上，onlyOnLeftSide和onlyOnCenter不能同时为true

  weight?: number; // 权重，用于排序，默认为 DEFAULT_LINE_WEIGHT;

  /** 视口过滤用的包围盒，缺省时按 seg 现算，见 IViewportFilterable.bounds */
  bounds?: BoundingBox;
}

export interface IAbsorbResult {
  absorbSegInfos: IAbsorbSegInfo[];
  absorbPoint: IPoint2d;
  rotation: number;
}

interface IParallelAbsorbResult {
  absorbSegInfos: IAbsorbSegInfo[];
  absorbPoint: IPoint2d;
  rotation: number;
}

export interface ICalculateOptions {
  extraBoxBoundingLineInfos?: IBoxBoundingLineInfo[];
  dynamicAbsorbDistance?: number;
}

const CENTER_LINE_SEG_LENGTH = 100; // 中心线线段长度，中心线不限制吸附在线段内，长度只用于表达方向

export const DEFAULT_LINE_WEIGHT = 100;
export const PREFER_SIDE_LINE_WEIGHT = 110;
export const CENTER_LINE_WEIGHT = 90;
export const BOX_BOUNDING_LINE_WEIGHT = 80;

export class RotatedBoxAbsorbCalculator {
  private _absorbLineInfos: IBoxBoundingLineInfo[] = []; // 需要被吸附的平行线

  private _extraAbsorbLineInfos: IBoxBoundingLineInfo[] = [];

  /** 当前 calculate 的视口过滤矩形，null 表示不过滤（3D 场景走这条路径） */
  private _filterBounds: TAbsorbViewportBounds = null;
  private _getViewportBounds: TAbsorbViewportBoundsProvider;

  constructor(getViewportBounds: TAbsorbViewportBoundsProvider = getAbsorbViewportBounds) {
    this._getViewportBounds = getViewportBounds;
  }

  private get _totalAbsorbLineInfos() {
    // 只吸附视口内可见的线；extraBoxBoundingLineInfos 是按光标邻近现取的 DXF 底图线，不参与过滤
    return [...filterVisibleInfos(this._absorbLineInfos, this._filterBounds), ...this._extraAbsorbLineInfos];
  }

  updateData({
    absorbBoxBoundingLines,
    absorbCenterLines
  }: {
    absorbBoxBoundingLines: IBoxBoundingLineInfo[];
    absorbCenterLines: ICenterLineInfo[];
  }) {
    // 拷贝一份：下面会往里 push 中心线，不能污染调用方传入的数组
    this._absorbLineInfos = [...absorbBoxBoundingLines];

    absorbCenterLines.forEach((item) => {
      const center = item.lineCenter;
      // 中心线可以是任意方向（box旋转时中心线跟着旋转），直接取line的方向构造线段
      const { direction } = item.line.parametricForm;
      const seg = new LineSeg2d(center, Point2dUtils.addDistance(center, CENTER_LINE_SEG_LENGTH, direction));
      this._absorbLineInfos.push({
        seg, // 默认start就是其他box的中心
        line: item.line,
        rotatable: false,
        onlyAbsorbSeg: false,
        onlyOnCenter: true,
        angle: item.angle,
        weight: CENTER_LINE_WEIGHT,
        bounds: item.bounds
      });
    });
  }

  calculate(rawBox: IRotatedBox, options?: ICalculateOptions): IAbsorbResult | undefined {
    this._extraAbsorbLineInfos = options?.extraBoxBoundingLineInfos ?? []; // 动态添加的包围线
    const dynamicAbsorbDistance = options?.dynamicAbsorbDistance ?? globalStores.dynamicAbsorbDistance;

    // getNeighborSegInfos 每次 calculate 要跑两遍（第一/第二条吸附线），过滤矩形只算一次
    this._filterBounds = getAbsorbFilterBounds(this._getViewportBounds(), [rawBox.center]);

    const absorbInfo = this.calculateAbsorb(rawBox, dynamicAbsorbDistance);

    return absorbInfo;
  }

  private getNeighborSegInfos(rotatedBox: IRotatedBox, dynamicAbsorbDistance: number): IAbsorbSegInfo[] {
    const { center, width, thickness, rotation, preferAbsorbSide } = rotatedBox;

    let boxXDirection = { x: 1, y: 0 };
    let boxYDirection = { x: 0, y: 1 };
    boxXDirection = Point2dUtils.rotateDirection(boxXDirection, rotation);
    boxYDirection = Point2dUtils.rotateDirection(boxYDirection, rotation);
    const boxXAngle = normalizeAngle(Point2dUtils.getAngle({ x: 0, y: 0 }, boxXDirection));
    const boxYAngle = normalizeAngle(Point2dUtils.getAngle({ x: 0, y: 0 }, boxYDirection));

    // 初步过滤出需要计算的吸附线段；
    const neighborSegInfos: IAbsorbSegInfo[] = [];
    this._totalAbsorbLineInfos.forEach(
      ({ seg, rotatable, onlyAbsorbSeg, onlyOnLeftSide, onlyOnCenter, angle: segAngle, weight: lineWeight }) => {
        // 如果只能吸附在左侧，且center在右侧，则不计算
        const side: EPointOnLineSide = onlyOnCenter ? EPointOnLineSide.On : Math.sign(seg.pointLineSegSide(center));
        if (onlyOnLeftSide && side === EPointOnLineSide.Right) return;

        // 如果该线段不能旋转box且是非中心点吸附时，检查是否平行box的xy轴
        if (!onlyOnCenter && !rotatable) {
          const parallelToBoxX = isAngleEqual(segAngle, boxXAngle, true);
          const parallelToBoxY = isAngleEqual(segAngle, boxYAngle, true);
          if (!parallelToBoxX && !parallelToBoxY) return;
        }

        // 如果只能吸附在线段，但垂足在线段外，则不计算
        const footRatio = seg.getFootRatio(center);
        if (onlyAbsorbSeg && (footRatio < 0 || footRatio > 1)) return;

        const foot = seg.getPointFromParameter(footRatio);
        const distance = Point2dUtils.distanceTo(foot, center);
        const targetToFootDistance = onlyOnCenter
          ? Point2dUtils.distanceTo(seg.start, foot)
          : getLineSegToPointDistance(seg, foot, footRatio);
        let absorbAxis, minRotationAngle;
        if (onlyOnCenter) {
          absorbAxis = EAbsorbAxis.Center;
          minRotationAngle = rotation;
        } else {
          const [_absorbAxis, deltaAngle] = getParallelAbsorbAxisAndMinRotationAngle(boxXAngle, boxYAngle, segAngle);
          absorbAxis = _absorbAxis;
          minRotationAngle = normalizeAngle(rotation + deltaAngle);
        }
        let absorbDistance =
          absorbAxis === EAbsorbAxis.Center ? 0 : absorbAxis === EAbsorbAxis.X ? thickness / 2 : width / 2;
        let diffDistance = Math.abs(distance - absorbDistance);
        let weight = lineWeight ?? DEFAULT_LINE_WEIGHT;
        let absorbOnPreferredSide = false;

        // 优先吸附侧处理
        if (!onlyOnCenter && rotatable && preferAbsorbSide) {
          const [preferAbsorbAxis, preferAbsorbRotationAngle] = getParallelAbsorbAxisAndMinRotationAngleWithPreferSide(
            boxXAngle,
            boxYAngle,
            seg,
            preferAbsorbSide,
            side
          );

          const preferAbsorbDistance = preferAbsorbAxis === EAbsorbAxis.X ? thickness / 2 : width / 2;
          const preferDiffDistance = Math.abs(distance - preferAbsorbDistance);
          if (preferDiffDistance < dynamicAbsorbDistance) {
            absorbAxis = preferAbsorbAxis;
            minRotationAngle = normalizeAngle(rotation + preferAbsorbRotationAngle);
            absorbDistance = preferAbsorbDistance;
            diffDistance = preferDiffDistance;
            weight = PREFER_SIDE_LINE_WEIGHT;
            absorbOnPreferredSide = true;
          }
        }

        if (diffDistance > dynamicAbsorbDistance) return;

        neighborSegInfos.push({
          seg,
          foot,
          angle: segAngle,
          diffDistance,
          targetToFootDistance,
          absorbDistance,
          side,
          axis: absorbAxis,
          rotationAngle: minRotationAngle,
          weight,
          absorbOnPreferredSide
        });
      }
    );

    neighborSegInfos.sort((a, b) => {
      if (a.weight !== b.weight) return b.weight - a.weight;
      if (!equalsNumber(a.diffDistance, b.diffDistance)) return a.diffDistance - b.diffDistance;
      return a.targetToFootDistance - b.targetToFootDistance;
    });

    return neighborSegInfos;
  }

  private calculateAbsorb(rawBox: IRotatedBox, dynamicAbsorbDistance: number): IParallelAbsorbResult | undefined {
    let neighborSegInfos: IAbsorbSegInfo[] = this.getNeighborSegInfos(rawBox, dynamicAbsorbDistance);
    if (neighborSegInfos.length === 0) return;

    // === 最邻近线段进行一次计算 ===
    const firstNeighborSegInfo = neighborSegInfos[0];
    let firstAbsorbInfo: IParallelAbsorbResult;
    if (firstNeighborSegInfo.axis === EAbsorbAxis.Center) {
      // firstNeighborSegInfo.seg.start 默认为box中心
      firstAbsorbInfo = {
        absorbPoint: firstNeighborSegInfo.foot,
        rotation: firstNeighborSegInfo.rotationAngle,
        absorbSegInfos: [firstNeighborSegInfo]
      };
    } else {
      firstAbsorbInfo = getParallelAbsorbInfo(firstNeighborSegInfo);
    }

    // === 查找第二条邻近线段（需要垂直于第一条） ===
    // 第二条吸附线段是否可旋转，如果第一条是不可旋转的，需要考虑第二条的吸附旋转，在box有优先吸附侧时才生效
    const secondAbsorbRotatedBox: IRotatedBox = {
      rotation: firstAbsorbInfo.rotation,
      center: firstAbsorbInfo.absorbPoint,
      width: rawBox.width,
      thickness: rawBox.thickness
    };
    // 如果第一条吸附线段在box中心 或者 没有吸附在box的优先吸附侧对应的轴上，允许第二条吸附线段改变旋转方向
    const allowSecondAbsorbRotation =
      firstAbsorbInfo.absorbSegInfos[0].axis === EAbsorbAxis.Center ||
      (rawBox.preferAbsorbSide && absorbBoxSideToAxis(rawBox.preferAbsorbSide) !== firstNeighborSegInfo.axis);
    if (allowSecondAbsorbRotation) secondAbsorbRotatedBox.preferAbsorbSide = rawBox.preferAbsorbSide;
    neighborSegInfos = this.getNeighborSegInfos(secondAbsorbRotatedBox, dynamicAbsorbDistance); // 根据firstAbsorbInfo获取二次吸附线段数组
    const orthogonalAngle = limitAngle(firstNeighborSegInfo.angle + Math.PI / 2);
    // 第二条吸附线（含中心线）必须垂直于第一条，否则求交点会沿第一条线方向偏移 absorbDistance * cot(夹角)
    const secondAbsorbSegInfo = neighborSegInfos.find((info) => {
      if (isAngleEqual(info.angle, firstNeighborSegInfo.angle, true)) return false;

      return Math.abs(info.angle - orthogonalAngle) < RADIAN_ANGLE_THRESHOLD;
    });

    if (secondAbsorbSegInfo) {
      let secondAbsorbInfo;
      let rotation = firstAbsorbInfo.rotation;
      const firstSegDirection = Point2dUtils.getUnitDirection(
        firstNeighborSegInfo.seg.start,
        firstNeighborSegInfo.seg.end
      );

      if (firstNeighborSegInfo.axis === EAbsorbAxis.Center && secondAbsorbSegInfo.axis === EAbsorbAxis.Center) {
        // 吸附了两条中心线
        const intersectionPoint = firstNeighborSegInfo.seg
          .createLine()
          .intersection(secondAbsorbSegInfo.seg.createLine());

        if (!intersectionPoint) return;

        firstAbsorbInfo.absorbPoint = intersectionPoint;
        firstAbsorbInfo.absorbSegInfos.push(secondAbsorbSegInfo);
        firstAbsorbInfo.absorbSegInfos[0].seg = new LineSeg2d(
          firstAbsorbInfo.absorbSegInfos[0].seg.start,
          intersectionPoint
        );
      } else if (secondAbsorbSegInfo.axis === EAbsorbAxis.Center) {
        // 第一条非中心线吸附，第二条中心线吸附
        const firstParallelLine = firstNeighborSegInfo.seg
          .getOffsetLineSeg(firstNeighborSegInfo.side * firstNeighborSegInfo.absorbDistance)
          .createLine();
        // seg.start为中心线所属box的中心，投影到第一条吸附线的平行线上即为吸附点（等价于两线垂直时的交点）
        firstAbsorbInfo.absorbPoint = firstParallelLine.pointProjectOnLine(secondAbsorbSegInfo.seg.start);
        firstAbsorbInfo.absorbSegInfos.push(secondAbsorbSegInfo);
      } else if (firstNeighborSegInfo.axis === EAbsorbAxis.Center) {
        // 第一条中心线吸附，第二条非中心线吸附
        const secondParallelLine = secondAbsorbSegInfo.seg
          .getOffsetLineSeg(secondAbsorbSegInfo.side * secondAbsorbSegInfo.absorbDistance)
          .createLine();
        // seg.start为中心线所属box的中心，投影到第二条吸附线的平行线上即为吸附点（等价于两线垂直时的交点）
        firstAbsorbInfo.absorbPoint = secondParallelLine.pointProjectOnLine(firstNeighborSegInfo.seg.start);
        firstAbsorbInfo.absorbSegInfos.push(secondAbsorbSegInfo);
        rotation = secondAbsorbSegInfo.rotationAngle;
      } else {
        secondAbsorbInfo = getParallelAbsorbInfo(secondAbsorbSegInfo);
        const firstAbsorbLine = new Line2d({
          parametric: { point: firstAbsorbInfo.absorbPoint, direction: firstSegDirection }
        });
        const secondFoot = firstAbsorbLine.pointProjectOnLine(secondAbsorbInfo.absorbPoint);
        firstAbsorbInfo.absorbPoint = secondFoot; // 根据secondFoot，更新firstAbsorbInfo的吸附点
        firstAbsorbInfo.absorbSegInfos.push(secondAbsorbSegInfo);
        rotation = secondAbsorbInfo.rotation;
      }

      if (allowSecondAbsorbRotation) firstAbsorbInfo.rotation = rotation;
    }

    return firstAbsorbInfo;
  }
}

function getLineSegToPointDistance(seg: LineSeg2d, point: IPoint2d, pointRatio: number) {
  if (pointRatio >= 0 && pointRatio <= 1) return 0;

  return Math.min(Point2dUtils.distanceTo(seg.start, point), Point2dUtils.distanceTo(seg.end, point));
}

function getParallelAbsorbInfo(segInfo: IAbsorbSegInfo) {
  const { seg, foot, absorbDistance, side, rotationAngle } = segInfo;
  const segDirection = Point2dUtils.getUnitDirection(seg.start, seg.end);
  const segNormalDirection = Point2dUtils.rotateDirection(segDirection, Math.PI / 2);

  return {
    absorbSegInfos: [segInfo],
    absorbPoint: Point2dUtils.addDistance(foot, absorbDistance * side, segNormalDirection),
    rotation: rotationAngle
  };
}

/*
 * 返回值中minRotationAngle为需要的旋转增量，并非旋转最终角度，如minRotationAngle为1，表box需要旋转1弧度
 * absorbAxis为吸附的轴（如吸附x轴，表示boxX方向和线段平行）
 */
function getParallelAbsorbAxisAndMinRotationAngle(boxXAngle: number, boxYAngle: number, segAngle: number) {
  const offsetAngle = RADIAN_ANGLE_THRESHOLD; // 添加偏移，主要用来处理两条垂直线段吸附同轴的问题

  let minRotationAngle: number;
  let absorbAxis: EAbsorbAxis;

  const segAngleRelativeToBoxX = normalizeAngle(segAngle - boxXAngle);
  if (segAngleRelativeToBoxX <= Math.PI / 4 + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX;
    absorbAxis = EAbsorbAxis.X;
  } else if (segAngleRelativeToBoxX <= Math.PI / 2 + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX - Math.PI / 2;
    absorbAxis = EAbsorbAxis.Y;
  } else if (segAngleRelativeToBoxX <= (Math.PI / 4) * 3 + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX - Math.PI / 2;
    absorbAxis = EAbsorbAxis.Y;
  } else if (segAngleRelativeToBoxX <= Math.PI + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX - Math.PI;
    absorbAxis = EAbsorbAxis.X;
  } else if (segAngleRelativeToBoxX <= (Math.PI / 4) * 5 + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX - Math.PI;
    absorbAxis = EAbsorbAxis.X;
  } else if (segAngleRelativeToBoxX <= (Math.PI / 4) * 6 + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX - (Math.PI / 4) * 6;
    absorbAxis = EAbsorbAxis.Y;
  } else if (segAngleRelativeToBoxX <= (Math.PI / 4) * 7 + offsetAngle) {
    minRotationAngle = segAngleRelativeToBoxX - (Math.PI / 4) * 6;
    absorbAxis = EAbsorbAxis.Y;
  } else {
    minRotationAngle = segAngleRelativeToBoxX - Math.PI * 2;
    absorbAxis = EAbsorbAxis.X;
  }

  return [absorbAxis, minRotationAngle] as const;
}

// 获取线段对齐box优先吸附侧时的轴和最小旋转角度
function getParallelAbsorbAxisAndMinRotationAngleWithPreferSide(
  boxXAngle: number,
  boxYAngle: number,
  seg: LineSeg2d,
  preferAbsorbSide: EBoxSide,
  boxCenterOnSegSide: EPointOnLineSide
) {
  const segAngle = normalizeAngle(Point2dUtils.getAngle(seg.start, seg.end));

  // center在seg左侧时，从center指向seg的方向 = segAngle - PI/2；右侧则 = segAngle + PI/2
  const towardSegAngle =
    boxCenterOnSegSide === EPointOnLineSide.Right
      ? normalizeAngle(segAngle + Math.PI / 2)
      : normalizeAngle(segAngle - Math.PI / 2);

  let rotationAngle: number;
  let absorbAxis: EAbsorbAxis;

  let alineAxisAngle: number; // box对应的对齐方向的角度
  switch (preferAbsorbSide) {
    case EBoxSide.Top: // +Y朝向seg → X轴平行于seg
      absorbAxis = EAbsorbAxis.X;
      alineAxisAngle = boxYAngle;
      break;
    case EBoxSide.Bottom: // -Y朝向seg → X轴平行于seg
      absorbAxis = EAbsorbAxis.X;
      alineAxisAngle = normalizeAngle(boxYAngle + Math.PI);
      break;
    case EBoxSide.Right: // +X朝向seg → Y轴平行于seg
      absorbAxis = EAbsorbAxis.Y;
      alineAxisAngle = boxXAngle;
      break;
    case EBoxSide.Left: // -X朝向seg → Y轴平行于seg
      absorbAxis = EAbsorbAxis.Y;
      alineAxisAngle = normalizeAngle(boxXAngle + Math.PI);
      break;
  }

  rotationAngle = normalizeAngle(towardSegAngle - alineAxisAngle);
  if (rotationAngle > Math.PI) rotationAngle -= Math.PI * 2;

  return [absorbAxis, rotationAngle] as const;
}

// 判断两个角度是否平行
function isAngleEqual(angle1: number, angle2: number, ignoreDirection: boolean = false) {
  const normalizedAngle1 = normalizeAngle(angle1);
  const normalizedAngle2 = normalizeAngle(angle2);

  if (equalsNumber(normalizedAngle1, normalizedAngle2)) return true;
  if (!ignoreDirection) return false;

  const oppositeAngle2 = normalizeAngle(normalizedAngle2 + Math.PI);
  return equalsNumber(normalizedAngle1, oppositeAngle2);
}

function absorbBoxSideToAxis(preferAbsorbSide: EBoxSide) {
  switch (preferAbsorbSide) {
    case EBoxSide.Top:
    case EBoxSide.Bottom:
      return EAbsorbAxis.X;
    case EBoxSide.Right:
    case EBoxSide.Left:
      return EAbsorbAxis.Y;
  }
}
